import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import MobileFrame from "../../components/MobileFrame";

import { useNavigate } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";

import type {
    RootState,
    AppDispatch,
} from "../../store";

import {
    setAuthToken,
    setOtpSession,
    clearOtpSession
} from "../../store/authSlice";

import {
    sendOtp,
    verifyOtp,
} from "../../api/auth.api";

import { ApiError } from "../../api/ApiError";

import Lottie from "lottie-react";

import loaderAnimation
    from "../../assets/Cleverpe_Loading.json";

import { fetchPincodeDetails } from "../../api/pincode.api";
import { setAddressPrefill } from "../../store/journeySlice";


const OTP_LENGTH = 6;
const RESEND_WAIT_SECONDS = 60;
const RESEND_TIMER_KEY = "otp_resend_available_at";


type Status =
    | "idle"
    | "verifying"
    | "resending"
    | "approved"
    | "error";


const getErrorMessage = (
    error: unknown,
    fallback: string
): string => {

    if (error instanceof ApiError) {
        return error.message || fallback;
    }

    if (error instanceof Error) {
        return error.message || fallback;
    }

    if (
        typeof error === "object" &&
        error !== null
    ) {

        const apiError =
            error as {
                response?: {
                    data?: {
                        message?: string;
                        error?: string;
                    };
                };
            };

        const message =
            apiError.response?.data?.message ||
            apiError.response?.data?.error;

        if (
            typeof message === "string" &&
            message.trim()
        ) {
            return message;
        }
    }

    return fallback;
};


const getRemainingResendTime = (): number => {
    const stored = sessionStorage.getItem(
        RESEND_TIMER_KEY
    );

    if (!stored) {
        return 0;
    }

    const availableAt = Number(stored);

    if (!Number.isFinite(availableAt)) {
        sessionStorage.removeItem(RESEND_TIMER_KEY);
        return 0;
    }

    const remaining = Math.ceil(
        (availableAt - Date.now()) / 1000
    );

    if (remaining <= 0) {
        sessionStorage.removeItem(RESEND_TIMER_KEY);
        return 0;
    }

    // Never allow an old/stale timer to show more
    // than the current configured cooldown.
    return Math.min(
        remaining,
        RESEND_WAIT_SECONDS
    );
};


const startResendCooldown = (): number => {
    const availableAt =
        Date.now() + RESEND_WAIT_SECONDS * 1000;

    sessionStorage.setItem(
        RESEND_TIMER_KEY,
        String(availableAt)
    );

    return RESEND_WAIT_SECONDS;
};


const OtpPage: React.FC = () => {

    const navigate = useNavigate();

    const dispatch =
        useDispatch<AppDispatch>();


    const journey =
        useSelector(
            (state: RootState) =>
                state.journey.journey
        );

    const otpToken =
        useSelector(
            (state: RootState) =>
                state.auth.otpToken
        );


    const [otp, setOtp] =
        useState<string[]>(
            Array(OTP_LENGTH).fill("")
        );

    const [status, setStatus] =
        useState<Status>("idle");

    const [errorMessage, setErrorMessage] =
        useState("");


    const [resendTimer, setResendTimer] =
        useState(
            getRemainingResendTime
        );


    const inputsRef =
        useRef<
            (HTMLInputElement | null)[]
        >([]);

    /*
 * =========================================================
 * AUTO FOCUS FIRST OTP INPUT
 * =========================================================
 */

    useEffect(() => {

        const frame =
            requestAnimationFrame(() => {

                inputsRef
                    .current[0]
                    ?.focus();

            });

        return () => {

            cancelAnimationFrame(
                frame
            );

        };

    }, []);


    const redirectTimeoutRef =
        useRef<ReturnType<typeof setTimeout> | null>(
            null
        );


    /*
     * Prevents duplicate API requests even if
     * the handler gets triggered multiple times
     * before React updates the button state.
     */
    const isSubmittingRef =
        useRef(false);

    /*
 * Ensures the address prefetch fires only once
 * per OTP page mount, even with re-renders.
 */
    const addressPrefetchStartedRef =
        useRef(false);


    const customerMobile =
        journey?.customerMobileMasked ?? "";


    /*
     * =========================================================
     * CLEANUP
     * =========================================================
     */

    useEffect(() => {

        return () => {

            if (
                redirectTimeoutRef.current
            ) {

                clearTimeout(
                    redirectTimeoutRef.current
                );

            }

        };

    }, []);


    /*
     * =========================================================
     * VALIDATE OTP SESSION
     * =========================================================
     */

    useEffect(() => {

        if (
            !journey ||
            !otpToken
        ) {

            navigate(
                "/Nlogin",
                {
                    replace: true,
                }
            );

        }

    }, [
        journey,
        otpToken,
        navigate,
    ]);

    /*
 * =========================================================
 * PREFETCH CITY/STATE FOR ADDRESS PAGE (GATEWAY FLOW ONLY)
 * =========================================================
 *
 * Fired here (not on the address page) so the
 * lookup happens while the user is occupied
 * entering the OTP, instead of showing a loader
 * on the address form later.
 */
    useEffect(() => {

        if (
            addressPrefetchStartedRef.current ||
            !journey ||
            journey.flowType !== "GATEWAY" ||
            !journey.customerPincode
        ) {
            return;
        }

        addressPrefetchStartedRef.current = true;

        (async () => {

            try {

                const result =
                    await fetchPincodeDetails(
                        journey.customerPincode
                    );

                if (result) {

                    dispatch(
                        setAddressPrefill({
                            city: result.city,
                            state: result.state,
                        })
                    );

                }

            } catch (error) {

                /*
                 * Silent failure — AddressForm
                 * falls back to manual entry.
                 */
                console.error(
                    "Address prefetch failed:",
                    error
                );

            }

        })();

    }, [
        journey,
        dispatch,
    ]);


    /*
     * =========================================================
     * RESEND TIMER
     * =========================================================
     */

    useEffect(() => {

        const updateTimer = () => {

            const remaining =
                getRemainingResendTime();

            setResendTimer(
                remaining
            );

        };


        updateTimer();


        const interval =
            window.setInterval(
                updateTimer,
                1000
            );


        return () => {
            window.clearInterval(
                interval
            );
        };

    }, []);


    /*
     * =========================================================
     * NAVIGATE ACCORDING TO JOURNEY PHASE
     * =========================================================
     */

    const navigateByJourneyPhase =
        useCallback(() => {

            if (!journey) {

                navigate(
                    "/Nlogin",
                    {
                        replace: true,
                    }
                );

                return;
            }


            switch (journey.phase) {

                case "ADDRESS":

                    navigate(
                        "/naddress",
                        {
                            replace: true,
                        }
                    );

                    break;


                case "PRE_APPROVAL":

                    navigate(
                        "/napproval",
                        {
                            replace: true,
                        }
                    );

                    break;


                case "SCHEME_SELECTION":

                    navigate(
                        "/nscheme-selection",
                        {
                            replace: true,
                        }
                    );

                    break;


                case "NATIVE":
                case "COMPLETED":

                    navigate(
                        "/ndashboard",
                        {
                            replace: true,
                        }
                    );

                    break;


                case "REJECTED":

                    navigate(
                        "/nrejected",
                        {
                            replace: true,
                        }
                    );

                    break;


                default:

                    setStatus("error");

                    setErrorMessage(
                        "We couldn't determine the next step of your application."
                    );

            }

        }, [
            journey,
            navigate,
        ]);


    /*
     * =========================================================
     * FORMAT TIMER
     * =========================================================
     */

    const formatTime =
        (seconds: number) => {

            const minutes =
                Math.floor(
                    seconds / 60
                );

            const remainingSeconds =
                seconds % 60;


            return `${minutes}:${remainingSeconds
                .toString()
                .padStart(2, "0")}`;

        };


    /*
     * =========================================================
     * OTP CHANGE
     * =========================================================
     */

    const handleChange = (
        value: string,
        index: number
    ) => {

        if (status === "verifying" ||
            status === "resending" ||
            status === "approved") {
            return;
        }


        /*
         * Keep only digits.
         */
        const digits =
            value.replace(/\D/g, "");


        if (!digits) {

            const next =
                [...otp];

            next[index] = "";

            setOtp(next);

            return;
        }


        /*
         * Handle pasted/multi-digit input.
         */
        const next =
            [...otp];

        let currentIndex =
            index;


        for (
            const digit of digits
        ) {

            if (
                currentIndex >= OTP_LENGTH
            ) {
                break;
            }

            next[currentIndex] =
                digit;

            currentIndex++;
        }


        setOtp(next);

        setErrorMessage("");

        setStatus("idle");

        lastAutoSubmittedRef.current = "";


        const focusIndex =
            Math.min(
                currentIndex,
                OTP_LENGTH - 1
            );


        inputsRef
            .current[focusIndex]
            ?.focus();

    };


    /*
     * =========================================================
     * OTP KEYBOARD
     * =========================================================
     */

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
        index: number
    ) => {

        if (e.key === "Backspace") {

            // Clear error as soon as user edits the OTP
            if (status === "error") {
                setStatus("idle");
                setErrorMessage("");
            }

            if (otp[index]) {

                const next = [...otp];

                next[index] = "";

                setOtp(next);

                lastAutoSubmittedRef.current = "";

                return;
            }

            if (index > 0) {

                const next = [...otp];

                next[index - 1] = "";

                setOtp(next);

                lastAutoSubmittedRef.current = "";

                inputsRef.current[index - 1]?.focus();
            }
        }

        if (
            e.key === "ArrowLeft" &&
            index > 0
        ) {
            inputsRef.current[index - 1]?.focus();
        }

        if (
            e.key === "ArrowRight" &&
            index < OTP_LENGTH - 1
        ) {
            inputsRef.current[index + 1]?.focus();
        }
    };


    /*
     * =========================================================
     * RESEND OTP
     * =========================================================
     */

    const handleResend = async () => {

        if (
            !journey ||
            resendTimer > 0 ||
            isSubmittingRef.current ||
            status === "approved"
        ) {
            return;
        }


        isSubmittingRef.current =
            true;


        try {

            setStatus("resending");

            setErrorMessage("");


            /*
             * Request a completely new OTP session.
             */
            const response =
                await sendOtp(
                    journey.journeyId
                );


            /*
             * Validate response before updating Redux.
             */
            if (
                !response?.data?.otpToken
            ) {

                throw new Error(
                    "Invalid OTP response received from the server."
                );

            }


            /*
             * Replace old OTP session.
             */
            dispatch(
                setOtpSession({
                    otpToken:
                        response.data.otpToken,

                    expiresInSeconds:
                        response.data
                            .expiresInSeconds,

                    /*
                     * Only keep devOtp during local development.
                     */
                    ...(import.meta.env.DEV &&
                        response.data.devOtp
                        ? {
                            devOtp:
                                response.data.devOtp,
                        }
                        : {}),
                })
            );


            /*
             * Clear previously entered OTP.
             */
            setOtp(
                Array(OTP_LENGTH).fill("")
            );


            /*
             * Restart cooldown only after
             * the API succeeds.
             */
            const timer =
                startResendCooldown();

            setResendTimer(
                timer
            );


            setStatus("idle");


            /*
             * Focus first OTP box.
             */
            requestAnimationFrame(() => {

                inputsRef
                    .current[0]
                    ?.focus();

            });

        } catch (error) {

            console.error(
                "OTP resend failed:",
                error
            );


            setStatus("error");


            setErrorMessage(
                getErrorMessage(
                    error,
                    "Unable to resend OTP. Please try again."
                )
            );

        } finally {

            isSubmittingRef.current =
                false;

        }

    };


    /*
     * =========================================================
     * VERIFY OTP
     * =========================================================
     */

    const handleVerify = async () => {

        if (
            isSubmittingRef.current ||
            status === "approved"
        ) {
            return;
        }


        if (!journey) {

            setStatus("error");

            setErrorMessage(
                "Your application session is unavailable. Please open the application link again."
            );

            return;
        }


        if (!otpToken) {

            setStatus("error");

            setErrorMessage(
                "Your OTP session has expired. Please resend the OTP."
            );

            return;
        }


        const enteredOtp =
            otp.join("");


        if (
            enteredOtp.length !== OTP_LENGTH
        ) {

            setStatus("error");

            setErrorMessage(
                "Please enter the complete 6-digit OTP."
            );

            return;
        }


        isSubmittingRef.current =
            true;


        try {

            setStatus("verifying");

            setErrorMessage("");


            const response =
                await verifyOtp(
                    journey.journeyId,
                    enteredOtp,
                    otpToken
                );


            if (
                !response?.data?.token
            ) {

                throw new Error(
                    "Authentication response is invalid."
                );

            }


            /*
             * Authentication successful.
             */
            dispatch(
                setAuthToken(
                    response.data.token
                )
            );


            /*
             * OTP session is no longer needed.
             */
            dispatch(clearOtpSession());


            sessionStorage.removeItem(
                "journey_session"
            );

            sessionStorage.removeItem(
                RESEND_TIMER_KEY
            );


            setStatus("approved");


            redirectTimeoutRef.current =
                setTimeout(() => {

                    navigateByJourneyPhase();

                }, 1000);


        } catch (error) {

            console.error(
                "OTP verification failed:",
                error
            );


            setStatus("error");


            setErrorMessage(
                getErrorMessage(
                    error,
                    "Invalid OTP. Please check the code and try again."
                )
            );


        } finally {

            isSubmittingRef.current =
                false;

        }

    };

    /*
 * Tracks the OTP string that was last
 * auto-submitted, so we don't fire
 * verify again for the same value
 * (e.g. on unrelated re-renders).
 */
    const lastAutoSubmittedRef =
        useRef<string>("");

    /*
     * =========================================================
     * AUTO VERIFY ON COMPLETE OTP
     * =========================================================
     *
     * Skips the extra tap — verifies as soon as
     * all 6 digits are filled.
     */
    useEffect(() => {

        const enteredOtp =
            otp.join("");

        if (
            enteredOtp.length !== OTP_LENGTH
        ) {
            return;
        }

        if (
            status === "verifying" ||
            status === "approved" ||
            status === "resending"
        ) {
            return;
        }

        if (
            lastAutoSubmittedRef.current ===
            enteredOtp
        ) {
            return;
        }

        lastAutoSubmittedRef.current =
            enteredOtp;

        handleVerify();

    }, [
        otp,
        status,
    ]);


    const isOtpComplete =
        otp.every(
            (digit) => digit !== ""
        );


    const isBusy =
        status === "resending" ||
        status === "verifying" ||
        status === "approved";


    return (

        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >

            <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                <div
                    aria-hidden="true"
                    className="absolute w-64 h-64 bg-purple-200 rounded-full top-[-90px] left-[-90px] blur-3xl opacity-20"
                />

                <div
                    aria-hidden="true"
                    className="absolute w-64 h-64 bg-blue-200 rounded-full bottom-[-120px] right-[-80px] blur-3xl opacity-20"
                />


                <div className="relative z-10 flex flex-col h-full px-6 pt-10">


                    {/* =================================================
                        HEADER
                    ================================================= */}

                    <div className="mb-8">

                        <h2 className="text-base font-bold text-gray-900">
                            Verify OTP
                        </h2>

                        <p className="mt-2 text-sm text-gray-500">

                            Enter the 6-digit code sent to{" "}

                            <span className="font-semibold text-indigo-600">
                                +91 {customerMobile || "your registered mobile"}
                            </span>

                        </p>

                    </div>


                    {/* =================================================
                        OTP INPUT
                    ================================================= */}

                    <div className="p-5 bg-white border border-gray-100 shadow-sm rounded-2xl">

                        <div
                            className="flex justify-center gap-2"
                            role="group"
                            aria-label="6-digit OTP"
                        >

                            {otp.map(
                                (value, index) => (

                                    <input
                                        key={index}

                                        ref={(element) => {
                                            inputsRef.current[index] =
                                                element;
                                        }}

                                        type="text"

                                        inputMode="numeric"

                                        autoComplete={
                                            index === 0
                                                ? "one-time-code"
                                                : "off"
                                        }

                                        aria-label={`OTP digit ${index + 1} of ${OTP_LENGTH}`}

                                        maxLength={OTP_LENGTH}

                                        value={value}

                                        onChange={(e) =>
                                            handleChange(
                                                e.target.value,
                                                index
                                            )
                                        }

                                        onKeyDown={(e) =>
                                            handleKeyDown(
                                                e,
                                                index
                                            )
                                        }

                                        disabled={isBusy}

                                        className="
                                            w-10
                                            h-12
                                            sm:w-11
                                            sm:h-14
                                            rounded-xl
                                            text-center
                                            text-lg
                                            font-medium
                                            text-gray-900
                                            border
                                            border-gray-200
                                            bg-white
                                            outline-none
                                            transition-all
                                            focus:border-indigo-500
                                            focus:ring-2
                                            focus:ring-indigo-100
                                            disabled:bg-gray-100
                                            disabled:cursor-not-allowed
                                        "
                                    />

                                )
                            )}

                        </div>

                    </div>


                    {/* =================================================
                        ERROR
                    ================================================= */}


                    {status === "error" && errorMessage && (
                        <div
                            role="alert"
                            aria-live="assertive"
                            className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2"
                        >
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-100">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    className="h-3.5 w-3.5 text-red-600"
                                >
                                    <circle cx="12" cy="12" r="9" />
                                    <path
                                        strokeLinecap="round"
                                        d="M12 8v4"
                                    />
                                    <path
                                        strokeLinecap="round"
                                        d="M12 16h.01"
                                    />
                                </svg>
                            </div>

                            <div className="flex-1">
                                <p className="text-xs font-semibold text-red-700">
                                    Invalid OTP
                                </p>

                                <p className="text-[11px] text-red-600">
                                    Please check the code and try again.
                                </p>
                            </div>
                        </div>
                    )}




                    {/* =================================================
                        RESEND
                    ================================================= */}

                    <div className="mt-6 flex items-center justify-center gap-1.5 text-sm">

                        <span className="text-gray-500">
                            Didn't receive the code?
                        </span>

                        <button
                            type="button"
                            onClick={handleResend}

                            disabled={
                                resendTimer > 0 ||
                                isBusy
                            }

                            className={`
                                font-semibold transition
                                ${resendTimer > 0 ||
                                    isBusy
                                    ? "cursor-not-allowed text-gray-400"
                                    : "text-indigo-600 hover:text-indigo-700"
                                }
                            `}
                        >

                            {status === "resending"
                                ? "Sending..."
                                : resendTimer > 0
                                    ? `Resend OTP in ${formatTime(resendTimer)}`
                                    : "Resend OTP"
                            }

                        </button>

                    </div>


                    {/* =================================================
                        LOADING OVERLAY
                    ================================================= */}

                    {(status === "resending" ||
                        status === "verifying" ||
                        status === "approved") && (

                            <div
                                className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white"
                                role="status"
                                aria-live="polite"
                            >

                                <div className="w-48 h-48">

                                    <Lottie
                                        animationData={
                                            loaderAnimation
                                        }

                                        loop={
                                            status !== "approved"
                                        }
                                    />

                                </div>


                                <p className="mt-4 text-indigo-600 font-semibold text-base">

                                    {status === "resending"
                                        ? "Sending new OTP..."
                                        : status === "verifying"
                                            ? "Verifying OTP..."
                                            : "OTP Verified"
                                    }

                                </p>


                                <p className="text-xs text-gray-400 mt-1">

                                    {status === "resending"
                                        ? "Please wait while we send a new code"
                                        : status === "verifying"
                                            ? "Please wait while we confirm your code"
                                            : "Redirecting..."
                                    }

                                </p>


                                <p className="absolute bottom-6 text-center text-[11px] font-semibold tracking-wide text-gray-400">
                                    CleverPe
                                </p>

                            </div>

                        )}


                    {/* =================================================
                        FOOTER
                    ================================================= */}

                    <div className="mt-auto pb-6 flex flex-col items-center space-y-4">

                        <img
                            src="/CleverPeLogos/CleverPe_Black_H.svg"
                            alt="CleverPe"
                            className="h-8 object-contain opacity-80"
                        />


                        <button
                            type="button"
                            onClick={handleVerify}

                            disabled={
                                !isOtpComplete ||
                                isBusy
                            }

                            className="
                                w-full
                                py-3
                                text-m
                                font-semibold
                                text-white
                                rounded-full
                                bg-gradient-to-r
                                from-indigo-500
                                to-blue-400
                                transition
                                hover:opacity-90
                                disabled:cursor-not-allowed
                                disabled:opacity-50
                            "
                        >

                            {status === "verifying"
                                ? "Verifying..."
                                : status === "approved"
                                    ? "Verified"
                                    : "Verify OTP"
                            }

                        </button>

                    </div>

                </div>

            </div>

        </MobileFrame>

    );

};


export default OtpPage;